/**
 * La projection réseau : une partie mise à plat, et remise en état.
 *
 * ## Pourquoi une forme à part
 *
 * L'état métier reste en objets TypeScript ordinaires, dans le moteur pur, et le
 * moteur n'importe jamais Colyseus (revue Codex, § 6.4) : sinon on hérite des
 * décorateurs, de la limite de 64 champs par `Schema` et des ruptures d'API de
 * `@colyseus/schema`. La Room détient la partie et la **projette**.
 *
 * Cette forme-ci est donc plate, sans `Map`, sans `Set`, sans champ optionnel : ce
 * qui traverse un `JSON.stringify` sans rien perdre, et ce qu'un `Schema` sait
 * porter.
 *
 * ## L'exigence qui commande tout
 *
 * **La scène doit pouvoir se reconstruire depuis l'état seul**, sans aucun événement
 * passé. Colyseus ne conserve que la dernière valeur d'une propriété par intervalle
 * de patch, et le handshake est sauté lors d'une reconnexion automatique : un journal
 * d'événements n'arrive pas intact, et bâtir l'affichage dessus revient à découvrir
 * le problème en production. D'où `reconstruire`, et le test d'aller-retour qui
 * l'exerce à chaque révision de parties entières.
 *
 * ## Un détail qui n'en est pas un : l'ordre des clés
 *
 * `memePartie` compare deux parties par leur JSON. Les objets reconstruits ici sont
 * donc écrits dans **l'ordre exact** où le moteur les crée. Ce n'est pas de la
 * coquetterie : à ordre différent, l'aller-retour serait juste et le test rouge, ou —
 * bien pire — on relâcherait la comparaison et on perdrait le seul contrôle qui
 * prouve qu'aucun champ ne s'est perdu.
 */
export class VueIncompatible extends Error {
}
/** Convention : les « absents » se codent par un nombre, pour tenir dans un Schema. */
const AUCUN = -1;
export function projeter(partie, demande) {
    return {
        revision: partie.revision,
        ruleSetId: partie.regles.id,
        phase: partie.phase,
        masse: partie.masse,
        joueurs: partie.joueurs.map((j) => ({
            siege: j.siege, nom: j.nom, jetons: j.jetons, estIA: j.estIA, niveauIA: j.niveauIA,
        })),
        actifs: [...partie.actifs],
        classement: [...partie.classement],
        manche: partie.manche ? projeterManche(partie.manche) : null,
        perdant: partie.perdant ?? AUCUN,
        demande,
    };
}
function projeterManche(manche) {
    return {
        numero: manche.numero,
        ouvreur: manche.ouvreur,
        lancersMax: manche.lancersMax ?? 0,
        tourDe: manche.tourDe ?? AUCUN,
        jetEnCours: manche.enCours !== null,
        des: manche.enCours?.des.map((d) => ({ id: d.id, valeur: d.valeur, garde: d.garde })) ?? [],
        lancers: manche.enCours?.lancers ?? 0,
        // Les poses sont indexées par siège dans l'état ; en tableau ici, parce qu'un
        // objet à clés numériques n'est pas un type de Schema.
        poses: Object.entries(manche.poses).map(([siege, pose]) => ({
            siege: Number(siege), main: [...pose.main], lancers: pose.lancers,
        })),
        rampeau: manche.rampeau
            ? {
                role: manche.rampeau.role,
                sens: manche.rampeau.sens,
                participants: [...manche.rampeau.participants],
                jets: Object.entries(manche.rampeau.jets).map(([siege, main]) => ({
                    siege: Number(siege), main: [...main],
                })),
            }
            : null,
        perdantDesigne: manche.resolution.perdant ?? AUCUN,
        donneurDesigne: manche.resolution.donneur ?? AUCUN,
        receveurDesigne: manche.resolution.receveur ?? AUCUN,
    };
}
/**
 * Refait une partie jouable à partir de sa projection.
 *
 * Les **événements** ne sont pas reconstruits, et c'est volontaire : ils sont
 * décoratifs et bornés (§ 3.2), et le réseau ne garantit pas de les livrer. Tout ce
 * qui compte pour jouer doit vivre dans les champs ci-dessus — c'est précisément ce
 * que le test d'aller-retour vérifie.
 */
export function reconstruire(vue, regles) {
    if (vue.ruleSetId !== regles.id) {
        throw new VueIncompatible(`partie reçue pour les règles « ${vue.ruleSetId} », en vigueur « ${regles.id} »`);
    }
    return {
        revision: vue.revision,
        regles,
        phase: vue.phase,
        masse: vue.masse,
        joueurs: vue.joueurs.map((j) => ({
            siege: j.siege, nom: j.nom, jetons: j.jetons, estIA: j.estIA, niveauIA: j.niveauIA,
        })),
        actifs: [...vue.actifs],
        classement: [...vue.classement],
        manche: vue.manche ? reconstruireManche(vue.manche) : null,
        perdant: vue.perdant === AUCUN ? null : vue.perdant,
        evenements: [],
    };
}
function reconstruireManche(vue) {
    const poses = {};
    // Triées par siège : les clés entières d'un objet JavaScript se parcourent dans
    // l'ordre croissant, et c'est cet ordre que le moteur produit naturellement.
    for (const pose of [...vue.poses].sort((a, b) => a.siege - b.siege)) {
        poses[pose.siege] = { main: pose.main, lancers: pose.lancers };
    }
    const resolution = {};
    // L'ordre d'affectation compte autant que le contenu : en décharge le moteur
    // désigne le donneur puis le receveur, jamais l'inverse.
    const mutable = resolution;
    if (vue.perdantDesigne !== AUCUN)
        mutable.perdant = vue.perdantDesigne;
    if (vue.donneurDesigne !== AUCUN)
        mutable.donneur = vue.donneurDesigne;
    if (vue.receveurDesigne !== AUCUN)
        mutable.receveur = vue.receveurDesigne;
    return {
        numero: vue.numero,
        ouvreur: vue.ouvreur,
        lancersMax: vue.lancersMax === 0 ? null : vue.lancersMax,
        tourDe: vue.tourDe === AUCUN ? null : vue.tourDe,
        enCours: vue.jetEnCours
            ? {
                des: vue.des.map((d) => ({
                    id: d.id, valeur: d.valeur, garde: d.garde,
                })),
                lancers: vue.lancers,
            }
            : null,
        poses,
        rampeau: vue.rampeau ? reconstruireRampeau(vue.rampeau) : null,
        resolution,
    };
}
function reconstruireRampeau(vue) {
    const jets = {};
    for (const jet of [...vue.jets].sort((a, b) => a.siege - b.siege)) {
        jets[jet.siege] = jet.main;
    }
    return {
        role: vue.role,
        sens: vue.sens,
        participants: [...vue.participants],
        jets,
    };
}
//# sourceMappingURL=vue.js.map